import React, { useState, useEffect } from 'react'
import { createConsumer } from '@rails/actioncable'
import { Progress } from '~/ui/progress'
import { Button } from '~/ui/button'
import { Badge } from '~/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { CheckCircle, XCircle, RefreshCw, Music, Download, Zap } from 'lucide-react'
import { toast } from '~/hooks/use-toast'

interface SyncProgressData {
  type: 'progress_update' | 'sync_complete' | 'sync_error'
  sync_history_id: string
  status: string
  message: string
  playlist_id?: string
  playlist_name?: string
  current_step?: string
  progress_percentage?: number
  total_tracks?: number
  current_track?: number
  current_track_name?: string
  action?: string
  tracks_added?: number
  tracks_updated?: number
  tracks_removed?: number
  tracks_imported?: number
  tracks_created?: number
  sync_type?: 'import' | 'sync'
  error?: string
}

interface SpotifySyncProgressProps {
  syncHistoryId?: string
  playlistId?: string
  playlistName?: string
  syncType?: 'import' | 'sync'
  onComplete?: (data: SyncProgressData) => void
  onError?: (error: string) => void
  compact?: boolean
}

const STEP_LABELS = {
  starting: 'Initializing...',
  fetching: 'Fetching playlist data',
  fetching_tracks: 'Fetching tracks',
  processing: 'Processing tracks',
  downloading_images: 'Downloading cover images',
  completed: 'Completed',
  failed: 'Failed',
}

const STEP_ICONS = {
  starting: RefreshCw,
  fetching: Download,
  fetching_tracks: Music,
  processing: Zap,
  downloading_images: Download,
  completed: CheckCircle,
  failed: XCircle,
}

export const SpotifySyncProgress: React.FC<SpotifySyncProgressProps> = ({
  syncHistoryId,
  playlistName,
  syncType = 'sync',
  onComplete,
  onError,
  compact = false,
}) => {
  const [progressData, setProgressData] = useState<SyncProgressData | null>(null)
  const [isVisible, setIsVisible] = useState(!!syncHistoryId)

  useEffect(() => {
    if (!syncHistoryId) return

    // Create ActionCable connection
    const cableUrl = (import.meta.env.VITE_CABLE_URL as string | undefined) || '/cable'
    const consumer = createConsumer(cableUrl)
    const channel = consumer.subscriptions.create('SpotifySyncChannel', {
      received: (data: SyncProgressData) => {
        console.log('Received sync progress:', data)

        // Only handle messages for our sync history
        if (data.sync_history_id === syncHistoryId) {
          setProgressData(data)
          setIsVisible(true)

          // If this is the first progress update we receive, log it
          if (!progressData) {
            console.log('Connected to ongoing sync operation:', {
              syncType: data.sync_type,
              status: data.status,
              message: data.message,
            })
          }

          // Handle completion
          if (data.type === 'sync_complete') {
            setTimeout(() => {
              setIsVisible(false)
              onComplete?.(data)

              // Show success toast
              toast({
                title: `${syncType === 'import' ? 'Import' : 'Sync'} completed!`,
                description: data.message,
              })
            }, 2000) // Show success state for 2 seconds
          }

          // Handle errors
          if (data.type === 'sync_error') {
            setTimeout(() => {
              setIsVisible(false)
              onError?.(data.error || 'Sync failed')

              // Show error toast
              toast({
                title: `${syncType === 'import' ? 'Import' : 'Sync'} failed`,
                description: data.error || 'An error occurred during the operation',
                variant: 'destructive',
              })
            }, 3000) // Show error state for 3 seconds
          }
        }
      },
    })

    return () => {
      if (channel) {
        channel.unsubscribe()
      }
      consumer.disconnect()
    }
  }, [syncHistoryId, onComplete, onError, syncType])

  const handleDismiss = () => {
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  // Show a loading state if we're waiting for progress data
  if (!progressData) {
    return (
      <div className="bg-card text-card-foreground flex items-center gap-3 rounded-lg border p-3">
        <RefreshCw className="text-primary h-4 w-4 animate-spin" />
        <span className="text-sm font-medium">Connecting to {syncType} progress...</span>
      </div>
    )
  }

  const {
    status,
    message,
    current_step,
    progress_percentage = 0,
    total_tracks,
    current_track,
    current_track_name,
    action,
    tracks_added,
    tracks_updated,
    tracks_removed,
    tracks_imported,
    tracks_created,
  } = progressData

  const StepIcon = STEP_ICONS[current_step as keyof typeof STEP_ICONS] || RefreshCw
  const stepLabel = STEP_LABELS[current_step as keyof typeof STEP_LABELS] || message

  if (compact) {
    return (
      <div className="bg-card text-card-foreground flex items-center gap-3 rounded-lg border p-3">
        <div className="flex items-center gap-2">
          <StepIcon
            className={`h-4 w-4 ${
              status === 'completed'
                ? 'text-green-500'
                : status === 'failed'
                  ? 'text-destructive'
                  : 'text-primary animate-spin'
            }`}
          />
          <span className="text-sm font-medium">
            {action && current_track
              ? `${action.charAt(0).toUpperCase() + action.slice(1)} track ${current_track}/${total_tracks}`
              : `${syncType === 'import' ? 'Importing' : 'Syncing'} ${playlistName || 'playlist'}`}
          </span>
        </div>

        <div className="flex-1">
          <Progress value={progress_percentage} className="h-2" />
        </div>

        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          {current_track && total_tracks && (
            <span>
              {current_track}/{total_tracks}
            </span>
          )}
          <span>{progress_percentage}%</span>
        </div>

        {status === 'completed' || status === 'failed' ? (
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            ×
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <StepIcon
            className={`h-5 w-5 ${
              status === 'completed'
                ? 'text-green-500'
                : status === 'failed'
                  ? 'text-destructive'
                  : 'text-primary animate-spin'
            }`}
          />
          <div className="flex-1">
            <CardTitle className="text-base">
              {syncType === 'import' ? 'Importing Playlist' : 'Syncing Playlist'}
            </CardTitle>
            <CardDescription className="text-sm">
              {playlistName || 'Spotify playlist'}
            </CardDescription>
          </div>
          {status === 'completed' || status === 'failed' ? (
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              ×
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{stepLabel}</span>
            <span className="font-medium">{progress_percentage}%</span>
          </div>
          <Progress value={progress_percentage} className="h-2" />
        </div>

        {/* Current track info */}
        {current_track && total_tracks && (
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {current_track} of {total_tracks} tracks
              </span>
            </div>
            {current_track_name && (
              <p className="text-muted-foreground truncate text-xs">
                {action ? `${action.charAt(0).toUpperCase() + action.slice(1)}:` : 'Processing:'}{' '}
                {current_track_name}
              </p>
            )}
          </div>
        )}

        {/* Completion stats */}
        {status === 'completed' && (
          <div className="flex flex-wrap gap-2 pt-2">
            {tracks_imported !== undefined && (
              <Badge variant="secondary" className="text-xs">
                {tracks_imported} imported
              </Badge>
            )}
            {tracks_created !== undefined && tracks_created > 0 && (
              <Badge variant="secondary" className="text-xs">
                {tracks_created} new
              </Badge>
            )}
            {tracks_added !== undefined && tracks_added > 0 && (
              <Badge variant="secondary" className="text-xs">
                {tracks_added} added
              </Badge>
            )}
            {tracks_updated !== undefined && tracks_updated > 0 && (
              <Badge variant="secondary" className="text-xs">
                {tracks_updated} updated
              </Badge>
            )}
            {tracks_removed !== undefined && tracks_removed > 0 && (
              <Badge variant="destructive" className="text-xs">
                {tracks_removed} removed
              </Badge>
            )}
          </div>
        )}

        {/* Error message */}
        {status === 'failed' && progressData.error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {progressData.error}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
